/* quotaEarnings.js -- A SALES REP'S MONTHLY BOOKINGS AND COMMISSION EARNED -- ONE OWNER (commission ship 1, 2026-10-09).

   runFullModel used to spell the same per-rep formula twice -- once for monthlyNewACV (the AE Quota revenue engine's
   input) and once for commissionData (what each rep EARNS) -- with identical quota x seasonal-pattern x ramp arithmetic.
   Both now call repMonthlyAcv below, so the two cannot drift (CLAUDE.md section 6, Principle 0).

   THE TWO SERIES
     buildMonthlyNewACV   FORECAST months only (m > the close), up to the termination month. Unchanged.
     buildCommissionData  EVERY month of the window (R12, Brian Q1 / Q2): commission earned in a CLOSED month can be paid
                          AFTER the close (a payment lag, or a cycle that straddles the close), so the engine needs it.
                          computePayroll books no payout dated on or before the close (the GL owns closed months).
                          After the termination month: an explicit 0 (unchanged).
   SCENARIO ATTAINMENT (Brian S1 (a), 10-09): `attainment` = { [employeeId]: percent } from runScenario. It scales the
     annual quota for months AFTER the close only -- revenue (forecast-only already) and commission alike. A closed month's
     commission always comes from the stored, unscaled quota. The scaled quota is r2(annual x percent / 100) -- the same
     arithmetic runScenario applied when it mutated annual_quota in place (ScenariosPage.js step 4b, retired 10-09), so every
     forecast figure of every stored scenario is unchanged.

   Plain ASCII on purpose (CLAUDE.md 12.5 rule 55). PURE. */
import { monthlyQuotaAmount } from './quotaDistribution';

const r2 = n => Math.round(n * 100) / 100;

const qmfOf = e => (e.quota_monthly_factors
  ? (typeof e.quota_monthly_factors === 'string' ? JSON.parse(e.quota_monthly_factors) : e.quota_monthly_factors)
  : {});

/* The rep's annual quota for month m: the stored quota, or -- after the close only -- the scenario's attained quota. */
function annualQuotaFor(e, m, cutoff, attainment) {
  const q = parseFloat(e.annual_quota) || 0;
  const att = attainment ? attainment[e.id] : undefined;
  if (att === undefined || att === null) return q;
  if (cutoff && m <= cutoff) return q;   // a closed month: the stored quota (S1 (a))
  return r2(q * att / 100);
}

/* THE ONE FORMULA: the month's booked ACV for a rep = r2(the month's quota amount x the ramp percentage).
   The ramp stays PER REP and multiplies AFTER the amount -- orthogonal to the company seasonal pattern. */
export function repMonthlyAcv(e, m, annualQuota, quotaPattern) {
  const monthlyAmt = monthlyQuotaAmount(annualQuota, quotaPattern, m);
  const rampSchedule = qmfOf(e).ramp_schedule || [];
  const startDate = e.startDate;
  let rampPct = 1;
  if (startDate) {
    const startM = startDate.slice(0, 7);
    if (m < startM) rampPct = 0;
    else if (rampSchedule.length > 0) {
      const [sy, sm] = startDate.split("-").map(Number);
      const [my, mm] = m.split("-").map(Number);
      const monthsSinceHire = (my - sy) * 12 + (mm - sm);
      if (monthsSinceHire < rampSchedule.length) rampPct = (rampSchedule[monthsSinceHire] || 0) / 100;
    }
  }
  return r2(monthlyAmt * rampPct);
}

/* monthlyNewACV: { 'YYYY-MM': total booked ACV across reps } -- forecast months only, up to each rep's termination month. */
export function buildMonthlyNewACV(employees, months, cutoff, quotaPattern, attainment) {
  const out = {};
  (employees || []).filter(e => e.is_sales_rep).forEach(e => {
    const termM = e.termDate ? e.termDate.slice(0, 7) : null;
    (months || []).forEach(m => {
      if (m <= cutoff) return;
      if (termM && m > termM) return;
      const acv = repMonthlyAcv(e, m, annualQuotaFor(e, m, cutoff, attainment), quotaPattern);
      out[m] = r2((out[m] || 0) + acv);
    });
  });
  return out;
}

/* commissionData: { [repId]: { 'YYYY-MM': commission EARNED } } -- every month of the window (R12); 0 after the termination
   month. Commission scales with booked ACV by construction: both come from repMonthlyAcv on the same quota. */
export function buildCommissionData(employees, months, cutoff, quotaPattern, attainment) {
  const out = {};
  (employees || []).filter(e => e.is_sales_rep).forEach(e => {
    const commRate = parseFloat(e.commission_rate) || 0;
    if (commRate <= 0) return;
    const termM = e.termDate ? e.termDate.slice(0, 7) : null;
    const empComm = {};
    (months || []).forEach(m => {
      if (termM && m > termM) { empComm[m] = 0; return; }
      const acv = repMonthlyAcv(e, m, annualQuotaFor(e, m, cutoff, attainment), quotaPattern);
      empComm[m] = r2(acv * commRate);
    });
    out[e.id] = empComm;
  });
  return out;
}
